const PARTY_TYPES = ['Client', 'Shipper/Consignee', 'Forwarder'];

/**
 * Trade partner roles the export can be cut by, in filter bar order. `types` are the partner
 * types offered in that role's dropdown. `shipmentTypes` lists where the role exists (FTL/LTL has no forwarder).
 */
export const TRADE_PARTNER_ROLES = [
    { key: 'client', label: 'Client', types: PARTY_TYPES, shipmentTypes: [0, 1, 2, 3, 4] },
    { key: 'shipper', label: 'Shipper', types: PARTY_TYPES, shipmentTypes: [0, 1, 2, 3, 4] },
    { key: 'consignee', label: 'Consignee', types: PARTY_TYPES, shipmentTypes: [0, 1, 2, 3, 4] },
    { key: 'forwarder', label: 'Forwarder', types: ['Forwarder'], shipmentTypes: [0, 1, 2, 3] },
    { key: 'trucker', label: 'Trucker', types: ['Trucker'], shipmentTypes: [0, 1, 2, 3, 4] },
];

export const EMPTY_TRADE_PARTNERS = Object.fromEntries(TRADE_PARTNER_ROLES.map(({ key }) => [key, []]));

/** Roles the picked shipment type has. */
export const rolesForShipmentType = (shipmentType) =>
    TRADE_PARTNER_ROLES.filter((role) => role.shipmentTypes.includes(shipmentType));

/**
 * The partner list split by role by partner type, each option named without the "Client - " prefix.
 * The type only decides who is offered; the export matches the role's shipment column, so a
 * partner registered as Forwarder picked under Client finds the shipments whose client it is.
 */
export const optionsByRole = (partners) => {
    const list = Array.isArray(partners) ? partners : [];
    return Object.fromEntries(
        TRADE_PARTNER_ROLES.map(({ key, types }) => [
            key,
            list
                .filter((p) => types.includes(p.partner_type))
                .map((p) => ({ id: p.id, name: p.partner_name ?? p.name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        ])
    );
};

const HOUSE_PARTIES = ['shipper', 'consignee'];

/**
 * Shipper/Consignee are written on one client's houses, so with a Client set they cannot take
 * several, and with several clients they cannot be used — the same rule as Rate Management.
 * The other way round, several shippers (or consignees) leave Client to one.
 */
export const multiSelectMode = (tradePartners) => {
    const clients = tradePartners.client ?? [];
    const houseSet = HOUSE_PARTIES.some((key) => (tradePartners[key] ?? []).length > 0);
    const manyHouse = HOUSE_PARTIES.some((key) => (tradePartners[key] ?? []).length > 1);
    return {
        clientMultiple: !houseSet,
        houseMultiple: clients.length === 0,
        clientBlocked: manyHouse,
        houseBlocked: clients.length > 1,
    };
};

/** Why these picks cannot be exported, or null. The server checks the same. */
export const tradePartnerConflict = (tradePartners, shipmentType) => {
    const roles = rolesForShipmentType(shipmentType);
    if (!roles.some(({ key }) => (tradePartners[key] ?? []).length > 0)) return 'Select at least one trade partner.';
    const clients = tradePartners.client ?? [];
    const house = HOUSE_PARTIES.map((key) => tradePartners[key] ?? []);
    if (clients.length > 0 && house.some((ids) => ids.length > 0)) {
        if (clients.length > 1 || house.some((ids) => ids.length > 1)) {
            return 'Client can be combined with Shipper/Consignee only when one of each is selected.';
        }
    }
    return null;
};

/** What the server gets: ids per role, only roles the shipment type has. */
export const tradePartnersPayload = (tradePartners, shipmentType) =>
    Object.fromEntries(
        rolesForShipmentType(shipmentType).map(({ key }) => [key, (tradePartners[key] ?? []).map((tp) => tp.id)])
    );
